import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import HttpClient from '../../api/api';
import {PIdNameModel, Project, Projects} from './types';
import {removeSessionCache} from '../../utils/session-cache';

const initialState: Projects = {
    types: [],
    items: [],
    offset: 0,
    total: 0,
    selectedTypeId: null,
    canCreateNewProject: false,
    canEditRoleForNotification: false,
    canEditProjectManager: false,
    canEditEmployees: false,
}

const PROJECTS_CACHE_KEY = 'cache:projects:filter:list';

const upsertProject = (state: Projects, project: Project) => {
    const selectedTypeId = state.selectedTypeId;
    const matchesSelectedType = selectedTypeId == null || selectedTypeId === project.projectType.id;
    const index = state.items.findIndex((item) => item.id === project.id);

    if (!matchesSelectedType) {
        if (index !== -1) {
            state.items.splice(index, 1);
            state.total = Math.max(0, state.total - 1);
        }
        return;
    }

    if (index !== -1) {
        state.items[index] = project;
        return;
    }

    state.items.push(project);
    state.total += 1;
}

export const getProjectTabs = createAsyncThunk(
    'projects-type/get-tabs',
    async () => {
        const response = await HttpClient.projectApi.getProjectTabs<PIdNameModel[]>();
        return response.data
    }
)

export const getProjects = createAsyncThunk(
    'projects/get',
    async (params: {projectTypeId: number, page: number}) => {
        const response = await HttpClient.projectApi.getProjects<Projects>(params.projectTypeId, params.page);
        return response.data
    }
)

export const getAllFullProjects = createAsyncThunk(
    'projects/get-all-full',
    async (page: number) => {
        const response = await HttpClient.projectApi.getAllFullProjects<Projects>(page);
        return response.data
    }
)

export const addProject = createAsyncThunk(
    'projects/create',
    async (project: Project) => {
        const response = await HttpClient.projectApi.add<Project>(project);
        removeSessionCache(PROJECTS_CACHE_KEY);
        return response.data
    }
)

export const updateProject = createAsyncThunk(
    'projects/update',
    async (project: Project) => {
        const response = await HttpClient.projectApi.update<Project>(project);
        removeSessionCache(PROJECTS_CACHE_KEY);
        return response.data
    }
)

export const deleteProject = createAsyncThunk(
    'projects/delete',
    async (id: number) => {
        const response = await HttpClient.projectApi.delete<Project>(id);
        removeSessionCache(PROJECTS_CACHE_KEY);
        return response.data
    }
)

export const getProject = createAsyncThunk(
    'projects/get-byid',
    async (id: number) => {
        const response = await HttpClient.projectApi.getById<Project>(id);
        return response.data;
    }
)

const typeSplice = createSlice({
    name: 'projects',
    initialState,
    reducers: {
        clearProjects: () => {
            return initialState;
        },
        setSelectedProjectType: (state, action: PayloadAction<number | null>) => {
            state.selectedTypeId = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getProjects.fulfilled, (state, action) => {
            if (state.selectedTypeId !== action.meta.arg.projectTypeId) {
                return;
            }

            if (action.payload.offset === 0) {
                state.items = action.payload.items;
                state.offset = action.payload.offset;
                state.total = action.payload.total;
                state.canCreateNewProject = action.payload.canCreateNewProject;
                state.canEditRoleForNotification = action.payload.canEditRoleForNotification;
                state.canEditProjectManager = action.payload.canEditProjectManager;
                state.canEditEmployees = action.payload.canEditEmployees;
            } else {
                state.items = [...state.items, ...action.payload.items];
                state.offset = action.payload.offset;
                state.total = action.payload.total;
            }
        });
        builder.addCase(getAllFullProjects.fulfilled, (state, action) => {
            if (action.payload.offset === 0) {
                state.items = action.payload.items;
                state.offset = action.payload.offset;
                state.total = action.payload.total;
                state.canCreateNewProject = action.payload.canCreateNewProject;
                state.canEditRoleForNotification = action.payload.canEditRoleForNotification;
                state.canEditProjectManager = action.payload.canEditProjectManager;
                state.canEditEmployees = action.payload.canEditEmployees;
            } else {
                state.items = [...state.items, ...action.payload.items];
                state.offset = action.payload.offset;
                state.total = action.payload.total;
            }
        });
        builder.addCase(getProjectTabs.fulfilled, (state, action) => {
            state.types = action.payload;
            const hasSelected = action.payload.some(type => type.id === state.selectedTypeId);
            if (!hasSelected) {
                const fallback = action.payload.find(type => type.isDefault) ?? action.payload[0] ?? null;
                state.selectedTypeId = fallback ? fallback.id : null;
            }
        });
        builder.addCase(addProject.fulfilled, (state, action) => {
            upsertProject(state, action.payload);
        });
        builder.addCase(updateProject.fulfilled, (state, action) => {
            upsertProject(state, action.payload);
        });
        builder.addCase(deleteProject.fulfilled, (state, action) => {
            const nextItems = state.items.filter((item) => item.id !== action.meta.arg);
            if (nextItems.length !== state.items.length) {
                state.items = nextItems;
                state.total = Math.max(0, state.total - 1);
            }
        });
    }
})

export const { clearProjects, setSelectedProjectType } = typeSplice.actions

export default typeSplice.reducer;
